/**
 * Starred filters (LPN-68): the filters somebody wants to get back to, kept per account.
 *
 * Lunarbor's Starred dialog is the model — a short, curated list of places, opened from a
 * star, that sends the pane you are in somewhere. Here a place is a filter: picking a
 * starred one *replaces* the window's filter rather than opening a new window, because a
 * window already is a saved filter (LPN-16) and the thing being asked for is "make this
 * window show that again".
 *
 * Everything here is pure; [StarredFiltersBackingViewModel] owns the state and the writes.
 *
 * ── What is starred, and what is not ─────────────────────────────────────────
 *
 * Every field of [WindowContent.BookmarkList] **except `columns`** — the same line *Reset*
 * draws (`BookmarkListBackingViewModel.onFilterReset`). The column count is about the
 * window's shape and the screen it is on, not about which rows it shows, so a starred
 * filter neither stores it nor overwrites it: [starrable] drops it to the default before
 * comparing or storing, and `onFilterReplaced` keeps the window's own.
 */
package se.soderbjorn.lunapin.client.viewmodel

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.decodeFromJsonElement
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject

/**
 * One starred filter.
 *
 * @property label what the row is called — the window's own name when it had one at the
 *   moment of starring, otherwise the filter's short summary. Fixed at starring time rather
 *   than derived on every paint, so a window renamed later does not rename the star it
 *   came from.
 * @property filter the filter, already [starrable] — `columns` is always the default.
 */
@Serializable
data class StarredFilter(
    val label: String,
    val filter: WindowContent.BookmarkList,
)

/** [this] filter as it is starred and compared: everything but the column count. */
fun WindowContent.BookmarkList.starrable(): WindowContent.BookmarkList =
    copy(columns = BookmarkColumns.AUTO)

/** Whether [filter] (columns ignored) is among [this]. */
fun List<StarredFilter>.containsFilter(filter: WindowContent.BookmarkList): Boolean {
    val key = filter.starrable()
    return any { it.filter == key }
}

/**
 * [this] with [filter] appended, labelled [label] — or unchanged when it is already
 * starred. Appended rather than prepended so the list keeps the order somebody built it in,
 * which is what Lunarbor's `Starred.md` does too.
 */
fun List<StarredFilter>.withFilter(filter: WindowContent.BookmarkList, label: String): List<StarredFilter> =
    if (containsFilter(filter)) this else this + StarredFilter(label, filter.starrable())

/** [this] without [filter] (columns ignored). */
fun List<StarredFilter>.withoutFilter(filter: WindowContent.BookmarkList): List<StarredFilter> {
    val key = filter.starrable()
    return filterNot { it.filter == key }
}

/**
 * The `lunapin.starredFilters.v1` blob, read and written.
 *
 * A versioned envelope like [WorkspaceCodec]'s, decoded **one entry at a time**: an entry a
 * later client wrote in a shape this one cannot read is dropped on its own, rather than
 * taking every other star with it. Never throws.
 */
object StarredFiltersCodec {

    private const val VERSION = 1

    @Serializable
    private data class Blob(
        val version: Int = VERSION,
        val starred: List<StarredFilter> = emptyList(),
    )

    /** The same settings as [WorkspaceCodec], so a filter is spelled identically in both blobs. */
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
        classDiscriminator = "kind"
    }

    /** The stored list, or empty for absent or unreadable. */
    fun decode(blob: String?): List<StarredFilter> {
        val text = blob?.takeIf { it.isNotBlank() } ?: return emptyList()
        val entries = runCatching {
            json.parseToJsonElement(text).jsonObject["starred"]?.jsonArray
        }.getOrNull() ?: return emptyList()
        return entries.mapNotNull { entry ->
            runCatching { json.decodeFromJsonElement<StarredFilter>(entry) }.getOrNull()
        }
    }

    /** Render [starred] to the blob the server stores. */
    fun encode(starred: List<StarredFilter>): String = json.encodeToString(Blob(starred = starred))
}
